import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({ 
  title, 
  description, 
  canonicalUrl,
  ogImage = '/media/j5tbshc2_Yetkin_logo_new.png',
  ogType = 'website',
  noIndex = false
}) => {
  const { language, t } = useLanguage();
  
  // Get localized title and description
  const localizedTitle = typeof title === 'object' ? t('title', { de: title.de, en: title.en }) : title;
  const localizedDescription = typeof description === 'object' ? t('description', { de: description.de, en: description.en }) : description;

  // Base URL for the site
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Structured data for the business
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Yetkin Dienstleistungen',
    description: language === 'de' 
      ? 'Professionelle Hospitality-Dienstleistungen für Hotels in Berlin und Brandenburg'
      : 'Professional hospitality services for hotels in Berlin and Brandenburg',
    url: siteUrl,
    logo: ogImage,
    image: ogImage,
    telephone: '+49 30 123456789',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Berlin',
      addressLocality: 'Berlin',
      addressRegion: 'Berlin',
      postalCode: '10115',
      addressCountry: 'DE'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 52.5200,
      longitude: 13.4050
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Berlin'
      },
      {
        '@type': 'State',
        name: 'Brandenburg'
      }
    ],
    serviceType: [
      'Housekeeping Services',
      'Front Office Services',
      'Concierge Services',
      'Window Cleaning',
      'Kitchen & Banquet Services'
    ],
    priceRange: '$$',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59'
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{localizedTitle}</title>
      <meta name="description" content={localizedDescription} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta charSet="utf-8" />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Language alternates */}
      <link rel="alternate" hrefLang="de" href={`${siteUrl}${typeof window !== 'undefined' ? window.location.pathname : ''}`} />
      <link rel="alternate" hrefLang="en" href={`${siteUrl}${typeof window !== 'undefined' ? window.location.pathname : ''}?lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${typeof window !== 'undefined' ? window.location.pathname : ''}`} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={localizedTitle} />
      <meta property="og:description" content={localizedDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={language === 'de' ? 'de_DE' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'de' ? 'en_US' : 'de_DE'} />
      <meta property="og:site_name" content="Yetkin Dienstleistungen" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={localizedTitle} />
      <meta name="twitter:description" content={localizedDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional Meta */}
      <meta name="theme-color" content="#003366" />
      <meta name="author" content="Yetkin Dienstleistungen" />
      <meta name="geo.region" content="DE-BE" />
      <meta name="geo.placename" content="Berlin" />
      
      {/* Keywords */}
      <meta name="keywords" content={language === 'de' 
        ? 'Housekeeping Berlin, Hotel Personal, Hospitality Service, Fensterreinigung, Front Office, Concierge Service, Hotel Reinigung, Yetkin'
        : 'Housekeeping Berlin, Hotel Staff, Hospitality Service, Window Cleaning, Front Office, Concierge Service, Hotel Cleaning, Yetkin'
      } />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
